import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardMedia, Typography, Grid, Box, Button, Switch } from '@mui/material';
import loadingGif from '../loading.gif';
import { useAuth } from '../AuthContext';

const Team = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [expandedMember, setExpandedMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await axios.get('https://sirius.runasp.net/api/Team', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setTeamMembers(response.data);
      } catch (error) {
        console.error('Error fetching team members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, [token]);

  if (loading) {
    return (
      <div style={{ margin: '50px auto', textAlign: 'center', width: '200px', height: '200px' }}>
        <img width={'100%'} src={loadingGif} alt="Loading..." />
      </div>
    );
  }

  const truncateSummary = (summary, limit = 20) => {
    const words = summary.split(' ');
    return words.length > limit ? `${words.slice(0, limit).join(' ')}...` : summary;
  };

  const handleReadMoreClick = (memberId) => {
    setExpandedMember(expandedMember === memberId ? null : memberId);
  };

  return (
    <Grid container spacing={2} justifyContent="start">
      {teamMembers.map((member, index) => (
        <Grid item key={member.memberId} xs={12} sm={8} md={6}>
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px',
              boxShadow: 3,
              borderRadius: 2,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: 6,
              },
            }}
          >
            <CardMedia
              component="img"
              image={`https://sirius.runasp.net/${member.photoPath}`}
              alt={member.userName}
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                border: '3px solid #fff',
                marginBottom: 2,
              }}
            />
            <CardContent sx={{ textAlign: 'center', padding: '10px', width: '100%' }}>
              <Typography variant="h6" component="div" sx={{ marginBottom: 1 }}>
                {member.userName}
              </Typography>
              <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
                {member.position}
              </Typography>
              <Typography variant="body2" sx={{ marginBottom: 2 }}>
                {expandedMember === member.memberId ? member.summary : truncateSummary(member.summary)}
              </Typography>
              <Button
                onClick={() => handleReadMoreClick(member.memberId)}
                sx={{ marginBottom: 2,
                  color: '#e84545', // Set your desired background color here
                  '&:hover': {
                  color: '#c73b3b', // Optional: Set background color on hover
                  }
                }}
              >
                {expandedMember === member.memberId ? 'Show Less' : 'Show More'}
              </Button>
              {expandedMember === member.memberId && (
                <Box
                  sx={{
                    fontSize: 12,
                    '& strong': {
                      color: 'coral',
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    <strong>Education:</strong> {member.education.universityName}, {member.education.degree}
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    <strong>Contact:</strong> {member.contacts.phoneNumber}, {member.contacts.email}
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    <strong>Skills:</strong> {member.skills.map(skill => skill.skillTitle).join(', ')}
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    <strong>Languages:</strong> {member.languages.map(language => `${language.languageTitle} (${language.level})`).join(', ')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Links:</strong> {member.links.map((link, index) => (
                      <a
                        key={index}
                        href={link.linkAddress}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'lightcoral', textDecoration: 'none', margin: '5px' }}
                      >
                        {link.linkAddress} <br />
                      </a>
                    ))}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
                <Typography variant="body2" sx={{ marginRight: 1 }}>
                  {member.visible ? 'Visible' : 'Hidden'}
                </Typography>
                <Switch
                  checked={member.visible}
                  inputProps={{ 'aria-label': 'visibility switch' }}
                  disabled
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#ff0000',
                      backgroundColor: 'rgba(255, 0, 0, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 0, 0, 0.4)',
                      },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#ff0000',
                    },
                    '& .MuiSwitch-switchBase.Mui-disabled.Mui-checked': {
                      color: '#ff0000', // lighter red for disabled checked state
                      backgroundColor: 'rgba(255, 0, 0, 0.1)',
                    },
                    '& .MuiSwitch-switchBase.Mui-disabled.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#ff0000',
                    },
                  }}
                
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default Team;
